/**
 * ZoneDrawingHud
 *
 * Fixed bottom-center overlay visible only during Zone polygon drawing.
 * Shows current point count and provides "완료"/"취소" controls.
 *
 * Keyboard shortcuts (registered while drawing):
 *   - Escape : cancel
 *   - Enter  : complete (if canComplete)
 *
 * Intentionally lightweight (inline styles) to avoid introducing a new
 * CSS module for a single overlay widget.
 */

import { useEffect, type CSSProperties } from 'react';

import {
  useZoneDrawingStore,
  ZONE_DRAWING_MIN_POINTS,
} from '../../../map-editor-shared/store/zoneDrawingStore';

interface ZoneDrawingHudProps {
  onComplete: () => void;
  onCancel: () => void;
}

const hudStyle: CSSProperties = {
  position: 'fixed',
  left: '50%',
  bottom: 24,
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 16px',
  background: 'rgba(31, 41, 55, 0.95)',
  color: '#f3f4f6',
  borderRadius: 10,
  boxShadow: '0 8px 28px rgba(0, 0, 0, 0.32)',
  zIndex: 1000,
  fontSize: 13,
  lineHeight: 1.2,
  userSelect: 'none',
};

const countStyle: CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
  minWidth: 64,
};

const buttonBase: CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  border: 'none',
  lineHeight: 1.2,
  transition: 'background-color 120ms ease',
};

const primaryButtonStyle: CSSProperties = {
  ...buttonBase,
  background: '#3A8EEE',
  color: '#ffffff',
};

const primaryDisabledStyle: CSSProperties = {
  ...buttonBase,
  background: '#4b5563',
  color: '#d1d5db',
  cursor: 'not-allowed',
};

const secondaryButtonStyle: CSSProperties = {
  ...buttonBase,
  background: 'transparent',
  color: '#e5e7eb',
  border: '1px solid rgba(229, 231, 235, 0.35)',
};

const hintStyle: CSSProperties = {
  color: '#9ca3af',
  fontSize: 12,
  marginLeft: 4,
};

const startButtonStyle: CSSProperties = {
  position: 'fixed',
  left: '50%',
  bottom: 24,
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 18px',
  background: '#3A8EEE',
  color: '#ffffff',
  border: 'none',
  borderRadius: 10,
  boxShadow: '0 8px 28px rgba(0, 0, 0, 0.32)',
  zIndex: 1000,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  userSelect: 'none',
};

interface ZoneRedrawButtonProps {
  zoneName: string;
  onStart: () => void;
}

/**
 * 구역이 선택됐을 때 보이는 진입 버튼. 누르면 그 구역의 폴리곤을 새로
 * 그리는 모드로 들어간다 — 자동 생성이 원으로 잡아 준 모양을 보고 직접
 * 다각형으로 고쳐 그리는 용도라, 소속(memberIds)은 그대로 두고 모양만 바꾼다.
 */
export function ZoneRedrawButton({ zoneName, onStart }: ZoneRedrawButtonProps) {
  return (
    <button
      type="button"
      data-testid="zone-redraw-button"
      style={startButtonStyle}
      onClick={onStart}
    >
      ✏️ &lsquo;{zoneName}&rsquo; 구역 다각형 다시 그리기
    </button>
  );
}

export function ZoneDrawingHud({ onComplete, onCancel }: ZoneDrawingHudProps) {
  const mode = useZoneDrawingStore((s) => s.mode);
  const pointCount = useZoneDrawingStore((s) => s.points.length);
  const isRedraw = useZoneDrawingStore((s) => s.replaceCellId != null);
  const canComplete = pointCount >= ZONE_DRAWING_MIN_POINTS;

  useEffect(() => {
    if (mode !== 'drawing') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        return;
      }
      if (e.key === 'Enter' && canComplete) {
        e.preventDefault();
        onComplete();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode, canComplete, onCancel, onComplete]);

  if (mode !== 'drawing') return null;

  return (
    <div
      role="toolbar"
      aria-label="Zone 드로잉 도구"
      data-testid="zone-drawing-hud"
      style={hudStyle}
    >
      {isRedraw && (
        <span style={{ fontWeight: 700, color: '#93c5fd' }}>다시 그리기</span>
      )}
      <span style={countStyle} data-testid="zone-drawing-hud-count">
        점 {pointCount}개
      </span>
      <button
        type="button"
        data-testid="zone-drawing-hud-complete"
        style={canComplete ? primaryButtonStyle : primaryDisabledStyle}
        disabled={!canComplete}
        aria-disabled={!canComplete}
        onClick={canComplete ? onComplete : undefined}
      >
        완료
      </button>
      <button
        type="button"
        data-testid="zone-drawing-hud-cancel"
        style={secondaryButtonStyle}
        onClick={onCancel}
      >
        취소
      </button>
      <span style={hintStyle} aria-hidden="true">
        ESC 취소 · Enter 완료 · 최소 {ZONE_DRAWING_MIN_POINTS}점 필요
      </span>
    </div>
  );
}

export default ZoneDrawingHud;
